<template>
  <div>
    <Dialog v-model:visible="visible" modal :closable="false" header="Import and Analyze Kustomer Conversation" :style="{ width: '60vw' }" :breakpoints="{ '1199px': '75vw', '575px': '90vw' }">
      <div class="mt-4 flex flex-col gap-2">
        <h2 class="text-lg font-semibold">Please provide Kustomer Conversation ID</h2>
        <label class="text-sm">Conversation ID</label>
        <InputText v-model="conversationId" class="" aria-describedby="docname-help" />
      </div>
      <div class="mt-4">
        <small class="block min-h-6">{{ progressText }} </small>
        <ProgressBar :mode="progressMode" style="height: 6px"></ProgressBar>
      </div>
      <div v-if="errorMessage" class="my-4 w-full">
        <Message :closable="false" severity="error">{{ errorMessage }}</Message>
      </div>
      <template #footer>
        <Button label="Cancel" :disabled="showProgress" severity="secondary" autofocus @click="visible = false" />
        <Button label="Import" :disabled="!conversationIdProvided || showProgress" severity="primary" autofocus @click="importConversation" />
      </template>
    </Dialog>
  </div>
</template>

<script setup>
  import { useSettingsStore } from '@/store/SettingsStore';
  import { useApi } from '@/hooks/useApi';
  import { useToast } from 'vue-toastification';
  import { ref, computed } from 'vue';
  import InputText from 'primevue/inputtext';
  import Dialog from 'primevue/dialog';
  import Button from 'primevue/button';
  import ProgressBar from 'primevue/progressbar';
  import Message from 'primevue/message';

  const settingsStore = useSettingsStore();
  const api = useApi();

  const toast = useToast();

  const visible = ref(false);
  const uploaded = ref(false);
  const showProgress = ref(false);
  const progressMode = computed(() => (showProgress.value ? 'indeterminate' : ''));
  const progressText = ref('');
  const errorMessage = ref(null);

  const conversationId = ref('');
  const conversationIdProvided = computed(() => conversationId.value && conversationId.value.length > 0);

  // eslint-disable-next-line no-unused-vars
  let kustomerConversation = null;
  let kustomerConversationTranscript = null;
  let kustomerConversationTranscriptBase64 = null;

  const emit = defineEmits(['kustomerConversationAnalyzed']);

  function start() {
    uploaded.value = false;
    progressText.value = '';
    showProgress.value = false;
    errorMessage.value = null;
    visible.value = true;
    kustomerConversation = null;
    kustomerConversationTranscript = null;
  }

  function getDateFolderUTC(date) {
    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0'); // getUTCMonth() returns month from 0-11, so we add 1
    const day = String(date.getUTCDate()).padStart(2, '0');

    return `${year}.${month}.${day}`;
  }

  async function moveFiles() {
    const transcriptConversationId = kustomerConversationTranscript.id;
    const transcriptPath = `${settingsStore.tenantId}/ingest/queue/uploads/${transcriptConversationId}.transcript.json`;
    const dateFolder = kustomerConversationTranscript?.conversationDay || getDateFolderUTC(kustomerConversationTranscript?.conversationDate ? new Date(kustomerConversationTranscript.conversationDate) : new Date());
    const prefix = kustomerConversationTranscript?.metadata?.kind ? `${kustomerConversationTranscript.metadata.kind}.` : '';
    const newTranscriptPath = `${settingsStore.tenantId}/transcripts/${dateFolder}/${prefix}${transcriptConversationId}.transcript.json`;
    await api.storeMoveFile({ path: transcriptPath, newPath: newTranscriptPath });
  }

  async function loadConversation() {
    progressText.value = 'Loading Conversation ...';
    showProgress.value = true;
    const loadConversationResult = await api.kustomerLoadConversation({
      conversationId: conversationId.value,
    });
    if (!loadConversationResult.success) {
      errorMessage.value = `Error loading conversation: ${loadConversationResult.error}`;
      progressText.value = '';
      showProgress.value = false;
      return null;
    }
    progressText.value = '';
    showProgress.value = false;
    kustomerConversation = loadConversationResult?.data?.conversation || null;
    kustomerConversationTranscript = loadConversationResult?.data?.conversationTranscript || null;
    kustomerConversationTranscriptBase64 = loadConversationResult?.data?.conversationTranscriptBase64 || null;
    return loadConversationResult.data;
  }

  async function uploadFiles() {
    if (!kustomerConversationTranscript || !kustomerConversationTranscriptBase64) {
      return null;
    }
    const transcriptConversationId = kustomerConversationTranscript.id;
    //const trUTF8 = new TextEncoder('utf-8').encode(JSON.stringify(kustomerConversationTranscript));
    //const trUTF8Str = String.fromCharCode(...trUTF8);
    //const transcriptBase64 = btoa(trUTF8Str);
    const transcriptPath = `${settingsStore.tenantId}/ingest/queue/uploads/${transcriptConversationId}.transcript.json`;
    const transcriptUploadRes = await api.storeUploadFile({
      path: transcriptPath,
      data: kustomerConversationTranscriptBase64,
      contentType: 'application/json',
    });
    if (!transcriptUploadRes.success) {
      errorMessage.value = `Error uploading file: ${transcriptUploadRes.error}`;
      return null;
    }
    return transcriptPath;
  }

  async function importConversation() {
    const jobId = settingsStore.transcriptIngestJobId;
    if (!jobId) {
      errorMessage.value = `Ingest job not configured`;
      return;
    }

    // eslint-disable-next-line no-unused-vars
    const conversationData = await loadConversation();
    if (!kustomerConversationTranscript) {
      errorMessage.value = `Error loading conversation data`;
      return;
    }

    errorMessage.value = null;
    progressText.value = 'Analyzing conversation ...';
    showProgress.value = true;

    const conversationTranscriptPath = await uploadFiles();
    if (!conversationTranscriptPath) {
      progressText.value = '';
      showProgress.value = false;
      return;
    }

    const jobParams = {
      jobId: jobId,
      jobParameters: {
        files: [conversationTranscriptPath],
      },
    };
    const jobResult = await api.executeJobOnDemand(jobParams);
    if (!jobResult.success) {
      errorMessage.value = `Error executing job: ${jobResult.error}`;
      progressText.value = '';
      showProgress.value = false;
      return;
    }

    await moveFiles();

    progressText.value = '';
    showProgress.value = false;
    visible.value = false;
    toast.success(`Conversation analyzed`);
    emit('kustomerConversationAnalyzed', jobResult);
  }

  defineExpose({ start });
</script>
